/**
 * Auswertung der Fehlerantworten der API.
 *
 * FastAPI antwortet je nach Fall unterschiedlich unter `detail`:
 *
 *   422  detail = [{ loc, msg, type }, …]        Prüffehler, eine Liste
 *   4xx  detail = "Text"                        eigene Meldung
 *   409  detail = { meldung, punkte_offen, … }  eigenes Objekt beim Abschluss
 *
 * Wer `detail` ungeprüft anzeigt, bekommt bei den ersten beiden Fällen
 * "[object Object]" auf den Schirm. Genau das ist hier einmal passiert, deshalb
 * geht jede Fehlerantwort durch diese Datei.
 */

export type Feldfehler = { feld: string; meldung: string }

export class ApiFehler extends Error {
  readonly status: number
  /** Fehler je Feld, aus einer 422-Antwort. Leer bei allen anderen. */
  readonly felder: Feldfehler[]
  /** Der rohe Inhalt von `detail`, falls ein Aufrufer mehr braucht. */
  readonly detail: unknown

  constructor(status: number, meldung: string, felder: Feldfehler[] = [], detail?: unknown) {
    super(meldung)
    this.name = 'ApiFehler'
    this.status = status
    this.felder = felder
    this.detail = detail
  }

  get istAnmeldefehler(): boolean {
    return this.status === 401
  }

  get istKonflikt(): boolean {
    return this.status === 409
  }

  /** Meldung für ein bestimmtes Eingabefeld, falls vorhanden. */
  fuerFeld(name: string): string | undefined {
    return this.felder.find((f) => f.feld === name)?.meldung
  }
}

/** Übersetzt die Fehlerkennungen von Pydantic in verständliche Sätze. */
const PYDANTIC_TEXTE: Record<string, string> = {
  missing: 'Diese Angabe fehlt',
  string_too_short: 'Die Angabe ist zu kurz',
  string_too_long: 'Die Angabe ist zu lang',
  value_error: 'Die Angabe ist nicht gültig',
  int_parsing: 'Bitte eine ganze Zahl eingeben',
  float_parsing: 'Bitte eine Zahl eingeben',
  date_from_datetime_parsing: 'Bitte ein Datum eingeben',
  enum: 'Bitte einen der angebotenen Werte wählen',
  greater_than_equal: 'Der Wert ist zu klein',
  less_than_equal: 'Der Wert ist zu groß',
}

type PydantikEintrag = {
  loc?: unknown[]
  msg?: string
  type?: string
}

/**
 * Der Ort eines Prüffehlers kommt als Liste, etwa ["body", "neues_passwort"].
 * "body" und "query" interessieren die Oberfläche nicht – gebraucht wird der
 * Feldname.
 */
function feldnameAus(loc: unknown[] | undefined): string {
  if (!Array.isArray(loc)) return ''
  const teile = loc.filter(
    (t): t is string | number => typeof t === 'string' || typeof t === 'number',
  )
  const ohneRahmen = teile.filter((t) => t !== 'body' && t !== 'query' && t !== 'path')
  return ohneRahmen.join('.')
}

function meldungAus(eintrag: PydantikEintrag): string {
  // Eine eigene Meldung aus einem model_validator ist immer die beste; Pydantic
  // stellt ihr "Value error, " voran.
  const roh = eintrag.msg ?? ''
  if (roh.startsWith('Value error, ')) return roh.slice('Value error, '.length)
  if (eintrag.type && PYDANTIC_TEXTE[eintrag.type]) return PYDANTIC_TEXTE[eintrag.type]
  return roh || 'Die Angabe ist nicht gültig'
}

/** Baut aus einer beliebigen Fehlerantwort einen ApiFehler. */
export function fehlerAusAntwort(status: number, rumpf: unknown): ApiFehler {
  const detail = (rumpf as { detail?: unknown } | null)?.detail ?? rumpf

  // 422: Liste von Prüffehlern
  if (Array.isArray(detail)) {
    const felder = detail
      .filter((e): e is PydantikEintrag => typeof e === 'object' && e !== null)
      .map((e) => ({ feld: feldnameAus(e.loc), meldung: meldungAus(e) }))

    const zusammen = felder.map((f) => f.meldung).join(' · ')
    return new ApiFehler(status, zusammen || 'Die Eingabe ist nicht gültig', felder, detail)
  }

  // Eigenes Objekt, etwa beim Abschluss mit offenen Punkten
  if (detail && typeof detail === 'object') {
    const alsObjekt = detail as Record<string, unknown>
    const meldung =
      typeof alsObjekt.meldung === 'string'
        ? alsObjekt.meldung
        : typeof alsObjekt.detail === 'string'
          ? alsObjekt.detail
          : 'Die Anfrage wurde abgelehnt'
    return new ApiFehler(status, meldung, [], detail)
  }

  if (typeof detail === 'string' && detail.length > 0) {
    return new ApiFehler(status, detail, [], detail)
  }

  return new ApiFehler(status, standardtext(status), [], detail)
}

function standardtext(status: number): string {
  if (status === 401) return 'Bitte neu anmelden'
  if (status === 403) return 'Dafür fehlt die Berechtigung'
  if (status === 404) return 'Nicht gefunden'
  if (status === 429) return 'Zu viele Versuche. Bitte kurz warten.'
  if (status >= 500) return 'Der Server hat einen Fehler gemeldet'
  return 'Die Anfrage ist fehlgeschlagen'
}

/** Meldung aus einem beliebigen geworfenen Wert – nie "[object Object]". */
export function fehlertext(fehler: unknown): string {
  if (fehler instanceof ApiFehler) return fehler.message
  if (fehler instanceof TypeError) {
    // fetch wirft TypeError, wenn keine Verbindung besteht.
    return 'Keine Verbindung zum Server'
  }
  if (fehler instanceof Error) return fehler.message
  if (typeof fehler === 'string') return fehler
  return 'Unbekannter Fehler'
}
